import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { MenuTabs } from '@/components/MenuTabs';
import { Navigation } from '@/components/Navigation';
import { NativeSelectSheet } from '@/components/NativeSelectSheet';
import { NotReadyFormGuard } from '@/components/NotReadyFormGuard';
import { TourOverlay } from '@/components/TourOverlay';
import { useActivityBeacon } from '@/lib/activity/useActivityBeacon';
import { resolveCanManage, resolveMenuCapability, useMenuCapability } from '@/lib/auth/permissions';
import { useSession } from '@/lib/auth/session';
import { useLedgerSaveListener } from '@/lib/excel/ledgerSaveBus';
import { useClosedMenuKeys } from '@/lib/menu/useMenuGate';
import { GUIDE_PARAM } from '@/lib/tour/guideLink';
import { runHelpOverride } from '@/lib/tour/helpOverride';
import { HOME_GUIDE_KEY, resolveGuide } from '@/lib/tour/pageGuides';
import { TOUR_KEY } from '@/lib/tour/steps';
import { useTourProgress } from '@/lib/tour/useTourProgress';
import { WHATS_NEW_STEPS, WHATS_NEW_TOUR_KEY, WHATS_NEW_VERSION } from '@/lib/tour/whatsNew';
import { useHolidayTable } from '@/lib/utils/holidays';
import { AgentChatProvider } from '@/pages/aiagent/AgentChatProvider';
import { inAgentModeFrame } from '@/pages/aiagent/agentModeFrame';
import { AgentSpotlight } from '@/pages/aiagent/AgentSpotlight';
import { AiAgentBubble } from '@/pages/aiagent/AiAgentBubble';
import { AiAgentDock } from '@/pages/aiagent/AiAgentDock';
import { StorageDock } from '@/pages/docs/dock/StorageDock';

/**
 * 로그인한 사용자가 보는 기본 골격 — 상단 헤더 + 좌측 사이드바 + 본문.
 *
 * v1 은 이 마크업이 섹션마다 있는 `layout.tsx` 9곳에 똑같이 복사돼 있었다.
 * SPA 는 라우트 트리에 레이아웃을 한 번만 얹으면 된다.
 *
 * AI Agent 대화는 넓은 화면을 쓰는 편이 낫다 — 그 경로에서는 사이드바를 숨기고
 * 본문의 왼쪽 여백도 함께 걷어 화면을 끝까지 쓴다.
 *
 * ## 사이드바 접기는 여기서 상태를 쥔다
 *
 * 데스크톱에서 사이드바를 접으면 본문의 왼쪽 여백({@code lg:pl-52})도 함께 걷혀야 한다. 사이드바를
 * 그리는 곳({@link Navigation})과 여백을 주는 곳(여기)이 갈려 있으므로, 접힘 상태는 둘의 공통 부모인
 * 여기서 쥐고 내려보낸다. 접힘은 데스크톱만의 개념이다 — 모바일은 여전히 서랍으로 여닫는다.
 *
 * <b>사이드바가 자리를 차지하는 지점은 lg(1024)다</b> — md(768)가 아니다. 아이패드 세로에서
 * 208px 을 내주면 홈 위젯 2열이 각 248px 로 눌려 할일 제목이 다섯 자만 남는다(사용자 지시
 * 2026-08-31). 그 폭에서는 사이드바가 서랍으로 물러나고 본문이 화면을 다 쓴다. 이 지점은
 * {@link WidgetMasonry} 의 2열 시작점과 <b>짝</b>이라 한쪽만 옮기면 다시 눌린다.
 *
 * ## 안내 투어도 여기서 쥔다
 *
 * 투어는 헤더·사이드바·본문을 함께 가리킨다. 그 셋의 공통 부모가 여기뿐이라 여는 버튼({@link Navigation}),
 * 설명할 화면({@code Outlet}), 어두운 막({@link TourOverlay})을 한자리에서 잇는다.
 *
 * <b>? 는 지금 보고 있는 화면의 안내를 연다</b>({@link resolveGuide}). 예전에는 어느 화면에서 눌러도
 * 홈으로 데려간 뒤 전체 안내를 틀었다 — 화면 사용법을 물은 사람을 다른 화면으로 끌고 가 엉뚱한 답을
 * 한 셈이다. 지금은 화면을 떠나지 않는다. 홈에서 누르면 처음 로그인 때와 같은 전체 안내가 나온다.
 *
 * ## 쉬운 모드는 여기서 걸지 않는다
 *
 * 한동안 {@code easy-mode} 클래스를 이 본문 전체에 걸었다. 그러면 <b>켠 적도 없는 화면</b>까지
 * 글씨와 버튼이 커진다 — 환경설정·회의록·결재처럼 앉아서 마우스로 쓰는 화면이 그렇다
 * (사용자 지적 2026-08-10). 켜고 끄는 버튼이 현장 조회 안에만 있으므로, 되돌릴 곳을 못 찾은 채
 * 커진 화면만 보게 된다.
 *
 * 그래서 클래스는 <b>그 버튼이 있는 화면</b>이 자기 뿌리에 건다({@link SitePmisPage}, DOC-31 §6).
 *
 * 진행 기록({@link useTourProgress})은 <b>전체 안내에만</b> 남긴다. 화면별 안내는 필요할 때 펴 보는
 * 설명서라 "봤다" 를 셀 것이 없고, 첫 로그인 자동 실행과 얽히면 홈이 아닌 화면에서 ? 를 한 번 눌렀다는
 * 이유로 전체 안내가 영영 뜨지 않는다.
 */

const COLLAPSE_KEY = 'bomi.sidebar.collapsed';

/**
 * <b>껍데기를 통째로 걷는</b> 화면들 — 상단바·사이드바·본문 여백이 전부 사라지고 화면을 끝까지 쓴다.
 *
 * <p>자료 뷰어(도면·PDF·3D)와 문서 편집기가 그렇다. 둘 다 <b>클수록 읽히는</b> 화면이고, 그 안에 자기
 * 도구줄(닫기·확장자 안내·편집기 리본)을 이미 들고 있어 우리 껍데기가 겹으로 얹힐 이유가 없다.
 * 예전 도면 뷰어는 껍데기 안에서 높이를 <b>끌어 늘리게</b> 했고 그 값을 기억까지 했다 — 매번 같은
 * 조작을 다시 하게 만드는 설계였다(DOC-41 §9).
 *
 * <p>AI 대화(`/aiagent`)는 사이드바만 접는다 — 그쪽은 상단바의 화면 이동을 계속 쓴다.
 */
const CHROMELESS_PATHS = ['/docs/view', '/docs/office', '/sheet', '/ledger'];

/** 첫 로그인 안내가 저절로 뜨는 자리. 홈 위젯도 설명 대상이라 다른 화면에서는 자동으로 뜨지 않는다. */
const HOME_PATH = '/home';

/**
 * 자료실 도크와 <b>겹치는</b> 화면 — 여기서만 도크를 뺀다(같은 목록이 두 벌로 뜬다).
 *
 * <p>예전에는 {@code /docs} <b>접두사 전부</b>를 뺐다. 그런데 그 아래에는 목록이 아닌 화면도 산다 —
 * 자료 요청(`/docs/submissions`) · 문서 분류 · 감사 기록 · 실험실. 그쪽은 겹치는 것이 없고, 특히
 * 자료 요청은 <b>무엇이 이미 올라와 있나</b>를 옆에 두고 답하는 자리라 도크가 필요한 화면이었다
 * (2026-09-07). 접두사로 자르면 «/docs 밑에 산다» 는 이유만으로 함께 잘린다.
 *
 * <p>{@code /docs} 자신(자료실 홈)은 <b>정확히 일치할 때만</b> 뺀다 — 접두사로 두면 이 목록이
 * 있으나 마나 해진다. 뷰어·편집기는 {@link CHROMELESS_PATHS} 가 이미 맡는다.
 */
const DOCS_BROWSER_PREFIXES = ['/docs/s3', '/docs/nas', '/docs/upload'];

/**
 * AI 도크를 쓸 수 있는 최소 화면 폭.
 *
 * <p><b>사이드바는 건드리지 않는다</b>(사용자 지시 2026-09-01). 한때 도크가 열릴 때 사이드바를
 * 자동으로 접게 했다가 뺐다 — 열었을 뿐인데 왼쪽 메뉴가 사라지면 사용자는 <b>무엇이 왜 없어졌는지</b>
 * 를 먼저 따지게 되고, 되돌릴 때 「원래 접혀 있었나」를 앱이 대신 판단해야 한다.
 *
 * <p>그래서 셋이 함께 선다. 그 값을 폭으로 치른다:
 * <pre>
 *   본문 = 화면폭 − 사이드바 208(w-52) − 도크 360 − 본문 여백 48
 *
 *   1920 → 1304    1600 → 984    1440 → 824    1366 → 750    1280 → 664
 * </pre>
 *
 * <p>1440 을 문턱으로 잡았다. 그 아래(1366·1280)는 본문이 750px 밑으로 떨어져 현장 정보의 표가
 * 가로로 밀린다 — <b>이 앱은 이미 그 폭을 「좁다」로 판정한 적이 있다.</b> 아이패드 세로에서
 * 사이드바 208px 을 내줬더니 홈 위젯 제목이 다섯 자만 남아 사이드바 기준점을 md 에서 lg 로 올렸다.
 *
 * <p>문턱 아래에서는 도크가 아예 없고 AI 버튼은 예전처럼 `/aiagent` 탭으로 간다.
 */
const DOCK_MIN_WIDTH = '(min-width: 1440px)';

/**
 * 도크가 열렸을 때 오른쪽에서 먹는 폭. 본문 여백 · 도크끼리의 자리 · 모달/서랍이 비켜설 폭이
 * <b>같은 값</b>이어야 해서 여기 한 곳에 둔다.
 */
const AI_DOCK_WIDTH = 360;
const STORAGE_DOCK_WIDTH = 420;

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

export function AppLayout() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  // AI Agent 모드의 오른쪽 칸 안에 뜬 앱도 껍데기를 걷는다(개발 빌드 전용 · `agentModeFrame`).
  const chromeless = CHROMELESS_PATHS.some((path) => pathname.startsWith(path)) || inAgentModeFrame();
  const showSidebar = !chromeless && !pathname.startsWith('/aiagent');

  // 화면 진입을 서버 활동 로그에 남긴다. 로그인 상태에서만, 경로가 바뀔 때만 보낸다.
  useActivityBeacon();

  /*
    다른 탭에서 대장 엑셀을 저장하면 이 탭의 표도 따라 바뀐다(2026-09-23). 듣는 자리가 여기인 것은
    화면이 바뀌어도 살아 있어야 하기 때문이다 — 화면마다 달면 탭을 옮기는 사이에 온 소식을 놓친다.
  */
  useLedgerSaveListener();

  /*
    공휴일 표를 여기서 한 번 받아 둔다(`lib/utils/holidays`). 달력·스케줄표 여러 곳이 그리는
    도중에 동기로 꺼내 쓰므로 채우는 자리는 그 전부의 위여야 하고, 그 자리가 여기다 —
    도착하면 이 컴포넌트가 다시 그려지며 아래 화면들이 공휴일과 함께 다시 그려진다.
  */
  useHolidayTable();

  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        // 저장을 못 해도 이번 세션 동안은 접힌 상태가 유지된다.
      }
      return next;
    });
  }, []);

  // ── AI 도크 ────────────────────────────────────────────────────────────────
  //
  // 도크는 `Outlet` **밖**에 산다. 화면이 바뀌어도 죽지 않아야 하기 때문이다 — 지금 AI 탭이
  // 전체 화면이라, 에이전트가 안내한 링크를 누르면 대화가 통째로 사라지는 것이 문제였다.
  const closedMenuKeys = useClosedMenuKeys();
  const [dockWide, setDockWide] = useState(() => window.matchMedia(DOCK_MIN_WIDTH).matches);
  const [dockOpen, setDockOpen] = useState(false);

  useEffect(() => {
    const query = window.matchMedia(DOCK_MIN_WIDTH);
    const sync = () => setDockWide(query.matches);
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);

  /**
   * 도크를 쓸 수 있는 자리인가.
   *
   * <b>홈은 뺀다.</b> 옆에 붙일 본문이 없고, 위젯 격자를 가리면서 좁은 패널을 여는 것보다 대화창을
   * 통째로 여는 편이 낫다(사용자 지시 2026-09-01). AI 탭 자신도 뺀다 — 같은 대화가 두 번 뜬다.
   */
  const dockAvailable =
    !chromeless &&
    !closedMenuKeys.has('ai_agent') &&
    dockWide &&
    pathname !== HOME_PATH &&
    !pathname.startsWith('/aiagent');

  /**
   * 떠 있는 동그란 AI 버튼이 서는가({@link AiAgentBubble}).
   *
   * <p><b>도크보다 넓은 조건</b>이다 — 홈에서도, 좁은 화면에서도 선다. 거기서는 도크가 못 서므로
   * 눌렀을 때 `/aiagent` 탭으로 가고, 버튼 자신은 늘 같은 자리에 남는다. 없어지는 자리는 둘뿐이다:
   * 이 사람에게 AI 가 아예 닫혀 있거나(오픈 게이트), 이미 그 화면에 있을 때.
   */
  const bubbleVisible = !closedMenuKeys.has('ai_agent') && !pathname.startsWith('/aiagent');

  const toggleDock = useCallback(() => {
    setDockOpen((prev) => !prev);
  }, []);

  // 열 수 없는 자리로 옮겼거나 화면이 좁아졌으면 닫는다. 열려 있는데 자리가 없으면 본문을
  // 밀어낸 여백만 남아 화면이 이유 없이 좁아 보인다.
  useEffect(() => {
    if (!dockAvailable) setDockOpen(false);
  }, [dockAvailable]);

  // ── 자료실 도크 ────────────────────────────────────────────────────────────
  //
  // AI 도크 <b>바로 옆</b>에 같은 방식으로 선다(사용자 지시 2026-09-04). 현장 정보·공지사항을
  // 보면서 자료를 찾고, 파일을 끌어다 대화창·서류칸에 참조로 놓는 것이 목적이라 — 여기서 화면을
  // 옮겨 버리면 그 목적이 사라진다. `Outlet` 밖인 이유도 AI 도크와 같다.
  const [storageDockOpen, setStorageDockOpen] = useState(false);
  /** 이 사람에게 자료실이 <b>메뉴로</b> 열려 있는가. 헤더 버튼도 그 판정을 그대로 따른다. */
  const canBrowseDocs = useMenuCapability('docs_browse', 'view');

  /**
   * 자료실 도크를 쓸 수 있는 자리인가.
   *
   * <p><b>자료 목록 화면에서는 뺀다</b>({@link DOCS_BROWSER_PREFIXES}) — 같은 목록이 두 벌로 뜨고,
   * 그 화면에는 도크가 안 맡는 일(폴더 도구·AI 학습)까지 이미 다 있다. 목록이 아닌 `/docs/*` 는
   * 겹치는 것이 없어 남긴다. 폭 문턱은 AI 도크와 같은 값을 쓴다({@code DOCK_MIN_WIDTH}) —
   * 그보다 좁으면 버튼이 아예 서지 않는다.
   *
   * <p><b>홈도 뺀다</b> — AI 도크와 같은 이유다(위 `dockAvailable`): 옆에 붙일 본문이 없고,
   * 위젯 격자를 420px 만큼 눌러 놓느니 자료실 화면을 통째로 여는 편이 낫다.
   */
  const onDocsBrowser =
    pathname === '/docs' ||
    DOCS_BROWSER_PREFIXES.some((path) => pathname === path || pathname.startsWith(`${path}/`));

  const storageDockAvailable =
    !chromeless &&
    // 오픈 게이트 — 아직 열지 않은 기능은 관리자 말고는 아무에게도 없다(V153).
    !closedMenuKeys.has('docs_browse') &&
    // 그리고 <b>메뉴와 같은 판정</b>이다(사용자 지시 2026-09-04). 왼쪽 메뉴에 자료실이 없는
    // 사람에게 이 버튼만 남으면, 눌러도 빈 목록을 보거나 403 을 만난다 — 화면이 «있다» 고
    // 말해 놓고 서버가 «없다» 고 답하는 자리를 만들지 않는다.
    canBrowseDocs &&
    dockWide &&
    pathname !== HOME_PATH &&
    !onDocsBrowser;

  const toggleStorageDock = useCallback(() => {
    setStorageDockOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!storageDockAvailable) setStorageDockOpen(false);
  }, [storageDockAvailable]);

  // 데스크톱에서 사이드바가 실제로 자리를 차지할 때만 본문 여백을 준다.
  //
  // ★ 도크는 이 값을 건드리지 않는다. 접고 펴는 것은 사용자의 일이고, 도크가 대신 접으면
  //   되돌릴 때 「원래 접혀 있었나」를 앱이 판단해야 한다(`DOCK_MIN_WIDTH` 머리말).
  const sidebarTakesSpace = showSidebar && !collapsed;
  // 도크도 같은 규칙 — 겹치지 않고 <b>민다.</b> 겹치면 표의 오른쪽 열이 가려지는데,
  // 사용자는 가려진 줄 모르고 읽는다. 금액 표에서 그건 사고다.
  const dockTakesSpace = dockAvailable && dockOpen;

  // 자료실 도크도 <b>겹치지 않고 민다</b> — AI 도크와 같은 규율이다(위 주석). 둘 다 열려 있으면
  // 폭을 더한다: 한쪽만 비켜 주면 나머지 하나가 본문 위에 얹힌다.
  const storageTakesSpace = storageDockAvailable && storageDockOpen;

  /*
    도크가 먹은 오른쪽 폭을 <b>문서 뿌리</b>에 적어 둔다.

    모달·서랍은 `document.body` 로 포털돼 나가므로({@link Modal}) 이 레이아웃이 본문에 준 여백을
    물려받지 못한다. 도크가 덮개보다 위에 서게 된 이상(`index.css` 의 `.dock-panel`), 이 값이
    없으면 창이 도크 밑에 깔린다 — 규칙을 CSS 변수 하나로 두면 비켜서야 하는 자리가 몇이든
    함께 맞는다(모달 덮개 · 현장 대화방 서랍).
  */
  /* 두 도크가 오른쪽에서 먹은 폭. CSS 변수와 떠 있는 오브가 <b>같은 값</b>을 본다. */
  const dockInset = (dockTakesSpace ? AI_DOCK_WIDTH : 0) + (storageTakesSpace ? STORAGE_DOCK_WIDTH : 0);

  useEffect(() => {
    const inset = dockInset;
    const root = document.documentElement;
    if (inset > 0) root.style.setProperty('--dock-inset', `${inset}px`);
    else root.style.removeProperty('--dock-inset');
    return () => {
      root.style.removeProperty('--dock-inset');
    };
  }, [dockInset]);

  const { shouldAutoStart, markSeen } = useTourProgress(TOUR_KEY);
  const [tourOpen, setTourOpen] = useState(false);
  // 자동 실행은 한 세션에 한 번이다. 홈을 몇 번 드나들어도 다시 뜨지 않는다.
  const autoStarted = useRef(false);

  /*
    지금 화면의 안내 대본. 홈이면 전체 안내와 같은 것이 돌아온다.

    <p><b>권한이 있어야 보이는 걸음은 여기서 거른다</b>(사용자 지시 2026-08-26). 보통은 «대상이
    화면에 없으면 알아서 빠진다» 로 충분하지만, 안내가 <b>대신 여는 판</b> 안의 요소는 시작할 때
    아직 없어서 그 규칙이 통하지 않는다 — 그 걸음들은 걸러내기의 예외라, 예외가 «등록 권한이 있어야
    보이는 버튼» 까지 살려 버린다. 조회만 하는 사람에게 「여기서 추가하세요」 가 뜨는 모양이다.

    <p>판정을 대본이 아니라 <b>여기서</b> 하는 이유: 대본은 값(데이터)이라 훅을 부를 수 없고,
    세션을 아는 것은 화면의 일이다.
  */
  const { data: session } = useSession();
  /*
    <b>한 탭만</b> 펴 달라는 부탁(`?guide=pmis-tab-daily` · 현장 조회의 「작성 안내가 필요하신가요?」).
    값이 그 탭 버튼의 `data-tour` 이름이고, 대본에서 그 탭을 여는 걸음만 남긴다. ? 버튼으로 열면 비운다.
  */
  const [guideTab, setGuideTab] = useState<string | null>(null);
  const guide = useMemo(() => {
    const resolved = resolveGuide(pathname);
    const allowed = resolved.steps.filter((step) => {
      const need = step.requires;
      if (!need) return true;
      return need.action === 'manage'
        ? resolveCanManage(session, need.menuKey)
        : resolveMenuCapability(session, need.menuKey, need.action);
    });
    const ofTab = guideTab ? allowed.filter((step) => step.openTabKey === guideTab) : [];
    return { ...resolved, steps: ofTab.length > 0 ? ofTab : allowed };
  }, [pathname, session, guideTab]);
  const isIntro = guide.key === HOME_GUIDE_KEY;

  useEffect(() => {
    if (autoStarted.current || !shouldAutoStart || pathname !== HOME_PATH) return;

    autoStarted.current = true;
    setTourOpen(true);
  }, [shouldAutoStart, pathname]);

  /*
    ── 「바뀐 것」 안내 (사용자 지시 2026-09-18 · {@link WHATS_NEW_STEPS}) ─────────────────

    <p>전체 안내와 <b>따로</b> 도는 짧은 안내다. 저쪽의 판번호를 올려 다시 띄우면 열한 걸음이
    통째로 되돌아오는데, 이미 쓰고 있는 사람에게 「메뉴 하나가 옮겨졌다」를 말하려고 처음부터
    다시 보게 할 수는 없다.

    <p>세 가지를 지킨다.
    <ul>
      <li><b>홈으로 데려간 뒤에 연다</b> — 짚는 것이 홈 위젯이라, 다른 화면에서 띄우면 대상이
          DOM 에 없어 그 걸음이 <b>조용히 걸러지고</b>({@link TourOverlay} 의 걸러내기) 하이라이트
          없는 카드만 뜬다. 「여기를 누르세요」 하면서 아무 데도 안 밝히는 안내가 된다.</li>
      <li><b>전체 안내에 양보한다</b> — 둘이 겹치면 어두운 막이 두 겹으로 깔린다.</li>
      <li><b>새로 들어온 사람에게는 안 띄운다</b> — 전체 안내를 아직 안 본 계정이 그 사람이고,
          「바뀌었습니다」 는 예전을 아는 사람에게만 뜻이 있는 말이다. 대신 조용히 본 것으로
          적어 둔다 — 안 그러면 전체 안내를 닫은 <b>바로 그 순간</b> 이 안내가 이어서 뜬다.</li>
    </ul>
  */
  const whatsNew = useTourProgress(WHATS_NEW_TOUR_KEY, WHATS_NEW_VERSION);
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const whatsNewHandled = useRef(false);
  // 데려가는 것은 <b>한 번뿐</b>이다. 홈에 못 닿는 계정(가드가 되돌리는 경우)이 생기면
  // 이 표시가 없을 때 이동이 끝없이 반복된다.
  const whatsNewRedirected = useRef(false);

  useEffect(() => {
    if (whatsNewHandled.current || !whatsNew.shouldAutoStart) return;

    if (shouldAutoStart) {
      // 처음 들어온 사람. 전체 안내가 곧 뜬다 — 이 안내는 보여 주지 않고 적기만 한다.
      whatsNewHandled.current = true;
      whatsNew.markSeen(true);
      return;
    }

    if (pathname !== HOME_PATH) {
      // 아직 문을 세우지 않는다(`whatsNewHandled`) — 홈에 도착한 다음 렌더에서 연다.
      if (whatsNewRedirected.current) return;
      whatsNewRedirected.current = true;
      navigate(HOME_PATH);
      return;
    }

    whatsNewHandled.current = true;
    setWhatsNewOpen(true);
  }, [whatsNew, shouldAutoStart, pathname, navigate]);

  const closeWhatsNew = useCallback(
    (completed: boolean) => {
      setWhatsNewOpen(false);
      whatsNew.markSeen(completed);
    },
    [whatsNew],
  );

  /**
   * <b>다른 곳에서 부탁한 안내</b>를 편다 — 지금은 AI 대화의 "○○ 사용법" 버튼이 그 부탁을 한다
   * (DOC-20 §1.18 · {@link GUIDE_PARAM}).
   *
   * <p>연 직후 주소에서 그 칸을 <b>지운다.</b> 남겨 두면 뒤로 가기·새로고침마다 안내가 다시 뜨고,
   * 그 화면을 즐겨찾기에 넣은 사람은 들어올 때마다 설명을 본다. `replace` 인 이유도 같다 —
   * 안내를 연 주소가 방문 기록에 한 칸을 차지하면 뒤로 가기가 그 자리로 되돌아온다.
   *
   * <p>대본 자체는 {@link resolveGuide} 가 경로로 고른다. 부탁하는 쪽은 "이 화면의 안내" 라고만
   * 말하고 <b>어느 대본인지는 말하지 않는다</b> — 대본 이름이 대화 쪽에 적히는 순간 두 곳이 된다.
   */
  useEffect(() => {
    const asked = new URLSearchParams(search).get(GUIDE_PARAM);
    if (asked === null) return;

    autoStarted.current = true;
    setGuideTab(asked.startsWith('pmis-tab-') ? asked : null);
    setTourOpen(true);

    const rest = new URLSearchParams(search);
    rest.delete(GUIDE_PARAM);
    const query = rest.toString();
    navigate({ pathname, search: query ? `?${query}` : '' }, { replace: true });
  }, [pathname, search, navigate]);

  const startTour = useCallback(() => {
    // 화면이 ? 를 대신 받겠다고 했으면 그쪽이 연다(현장 조회 수행관리 탭의 작성 안내 창 · helpOverride).
    if (runHelpOverride()) return;
    // 손으로 연 뒤에 자동 실행이 겹쳐 두 번 뜨지 않도록 여기서도 표시를 세운다.
    autoStarted.current = true;
    setGuideTab(null);
    setTourOpen(true);
  }, []);

  const closeTour = useCallback(
    (completed: boolean) => {
      setTourOpen(false);
      setGuideTab(null);
      // 화면별 안내는 세지 않는다 — 여기서 기록을 남기면 첫 로그인 전체 안내가 건너뛰어진다.
      if (isIntro) markSeen(completed);
    },
    [isIntro, markSeen],
  );

  // 껍데기를 걷은 화면은 상단바까지 없으므로 그만큼의 위 여백(pt-14)과 본문 여백도 함께 걷는다 —
  // 남겨 두면 화면 아래가 그 높이만큼 잘리고 스크롤바가 생긴다.
  if (chromeless) {
    // 껍데기를 걷은 화면에도 고르는 칸은 있다(뷰어의 배율·편집기 설정) — 시트는 함께 얹는다.
    //
    // ★ 여기도 `AgentChatProvider` 로 감싼다. 이 가지와 아래 가지에서 <b>같은 자리에 같은 타입</b>이
    //   서야 도면 뷰어를 열고 닫는 동안 대화가 살아남는다 — 한쪽만 감싸면 그 순간 언마운트된다.
    return (
      <AgentChatProvider>
        <Outlet />
        <NativeSelectSheet />
        <NotReadyFormGuard />
      </AgentChatProvider>
    );
  }

  return (
    <AgentChatProvider>
      <div className="min-h-screen">
        <Navigation
          showSidebar={showSidebar}
          collapsed={collapsed}
          onToggleCollapsed={toggleCollapsed}
          onStartTour={startTour}
          storageDockAvailable={storageDockAvailable}
          storageDockOpen={storageDockOpen}
          onToggleStorageDock={toggleStorageDock}
        />
        <div
          className={`pt-14 min-h-screen ${sidebarTakesSpace ? 'lg:pl-52' : ''} ${dockTakesSpace ? 'pr-[360px]' : ''}`}
          // 자료실 도크가 열려 있으면 두 도크의 폭을 <b>더해서</b> 준다 — 위 클래스만으로는
          // 한쪽 폭밖에 못 비켜, 나머지 하나가 본문 오른쪽 열을 덮는다.
          style={
            storageTakesSpace
              ? { paddingRight: STORAGE_DOCK_WIDTH + (dockTakesSpace ? AI_DOCK_WIDTH : 0) }
              : undefined
          }
        >
          {/*
            묶음 탭 — 사이드바에서 펼치던 하위 항목이 화면 맨 위로 올라왔다(사용자 지시 2026-09-22).

            <b>본문 밖</b>에 둔다(사용자 지시: 「패딩 주지말고 맨위로 붙여봐」). 안에 두면 본문 여백
            (p-4 md:p-6)이 위에 얹혀 탭 줄이 머리줄에서 한 뼘 떠 있고, 그 사이의 흰 칸이 「무엇도
            아닌 자리」로 남는다. 여기 두면 머리줄 바로 아래에 붙고 좌우로도 화면을 끝까지 쓴다.

            여기 한 곳에 두는 이유는 {@link MenuTabs} 머리말에 적었다: 무엇을 세울지는 주소가 이미
            말하고 있어서, 화면 백 곳을 고치지 않아도 전부 따라온다.
          */}
          <MenuTabs />
          <main className="p-4 md:p-6">
            <Outlet />
          </main>
        </div>

        {/*
          모바일의 <b>모든</b> 고르는 칸을 바텀시트로 띄운다(사용자 지시 2026-08-31). 화면마다 컴포넌트를
          갈아 끼우면 예순 곳 중 반드시 몇을 빠뜨리므로, 여기 하나만 얹고 나머지는 손대지 않는다.
        */}
        <NativeSelectSheet />
        <NotReadyFormGuard />

        {/* `Outlet` 밖이다 — 화면이 바뀌어도 이 컴포넌트는 안 죽는다. 그게 도크의 전부다. */}
        {dockAvailable && <AiAgentDock open={dockOpen} onClose={() => setDockOpen(false)} />}

        {/*
          AI 가 짚은 자리의 반짝임(사용자 지시 2026-09-23). <b>도크 밖·`Outlet` 밖</b>이다 —
          짚는 대상이 본문이고, 도크를 닫아도 짚어 준 것은 그대로 있어야 한다.

          도크를 쓸 수 없는 사람에게는 짚어 줄 대화 자체가 없다(`highlight` 가 늘 null).
        */}
        <AgentSpotlight />

        {/* 그 도크를 여는 손. 헤더가 아니라 오른쪽 아래에 늘 떠 있다(사용자 지시 2026-09-17). */}
        {bubbleVisible && (
          <AiAgentBubble
            dockAvailable={dockAvailable}
            dockOpen={dockOpen}
            onToggleDock={toggleDock}
            // 끌어 둔 자리가 도크 밑이면 그만큼 당겨 선다 — 도크가 오브보다 위에 있어(z-index)
            // 당기지 않으면 오브를, 그리고 닫을 손까지 함께 잃는다.
            rightInset={dockInset}
          />
        )}

        {/* 자료실 도크도 같은 자리·같은 이유다. AI 도크와 나란히 서고, 둘 다 열 수 있다. */}
        {storageDockAvailable && (
          <StorageDock
            open={storageDockOpen}
            onClose={() => setStorageDockOpen(false)}
            // AI 도크가 열려 있으면 그 왼쪽에 선다 — 헤더에서 「자료실」이 「AI Agent」 왼쪽인 것과 같은 차례다.
            offsetRight={dockTakesSpace ? AI_DOCK_WIDTH : 0}
          />
        )}

        {/* 경로가 키다 — 화면을 옮긴 뒤 다시 열면 걸음 번호가 1 부터 시작해야 한다. */}
        {tourOpen && (
          <TourOverlay
            key={guide.key}
            steps={guide.steps}
            finishLabel={isIntro ? '시작하기' : '확인'}
            onClose={closeTour}
          />
        )}

        {/* 전체 안내가 떠 있는 동안에는 물러선다 — 어두운 막이 두 겹으로 깔리면 아무것도 안 읽힌다. */}
        {whatsNewOpen && !tourOpen && (
          <TourOverlay steps={WHATS_NEW_STEPS} finishLabel="확인" onClose={closeWhatsNew} />
        )}
      </div>
    </AgentChatProvider>
  );
}
